import { RoundButton } from "@/components/common/CommonRoundButton";
import { LazoCheckbox } from "@/components/common/form/LazoCheckbox";
import CommonButton from "@/components/utils/CommonButton";
import { SimpleStarIcon } from "@/components/utils/icons/SimpleStarIcon";
import LazoTooltip from "@/components/utils/LazoTooltip";
import { useSubscriptionPurchase } from "@/contexts/SubscriptionPurchaseContext";
import { Colors } from "@/enums/Colors";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import { AddOnObjectKeyType, getAddOnMessage } from "@/models/subscriptions/AddOn";
import FunderListService from "@/services/FunderListService";
import { Funder, FunderList } from "@/types/FunderTypes";
import { GeneratedNameOrigin } from "@/types/GenerateSmartlistNameDTO";
import { Check, MoreHoriz } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  CircularProgress,
  InputAdornment,
  InputBase,
  inputBaseClasses,
  List,
  listClasses,
  ListItem,
  Menu,
  paperClasses,
  Skeleton,
  styled,
  Typography,
} from "@mui/material";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  hideAlreadyInLists?: boolean;
  onCreateNewFunderList: (name: string) => void;
  alreadyInFunderlists?: FunderList[];
  loading?: boolean;
  getMappedFilters?: () => Object;
  origin?: GeneratedNameOrigin;
  getFunders?: () => Funder[];
  iconButton?: boolean;
  funder?: Funder;
  smartlists?: FunderList[];
  closeOnMouseLeave?: boolean;
}

const debouncer = new Debouncer(1500);
const buttonDebouncer = new Debouncer(400);

const SaveToListButton: FC<Props> = ({
  onCreateNewFunderList,
  alreadyInFunderlists,
  loading,
  getMappedFilters,
  origin,
  getFunders: getFunderUuids,
  funder,
  iconButton,
  hideAlreadyInLists,
  smartlists,
  closeOnMouseLeave,
}) => {
  const [funderLists, setFunderLists] = useState<FunderList[]>(smartlists || []);
  const [funderSmartlists, setFunderSmartlists] = useState<FunderList[]>(alreadyInFunderlists || []);
  const [loadingAdding, setLoadingAdding] = useState<{
    [key: string]: boolean;
  }>({});

  const [anchor, setAnchor] = useState<null | (EventTarget & HTMLButtonElement)>(null);

  const [newSmartListName, setNewSmartListName] = useState<string>(null);
  const [loadingNames, setLoadingNames] = useState<boolean>(false);

  const [generatedNames, setGeneratedNames] = useState<string[]>([]);
  const { hasSmartListsCreation } = useSubscriptionPurchase();
  const { t } = useTranslation("fundraising");

  const handleCloseMenu = () => {
    setAnchor(null);
  };

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchor !== event.currentTarget) {
      setAnchor(event.currentTarget);
    }
    if (origin != null) {
      onClickIaStar();
    }
  };

  const id = "fundraising-table-hover-menu";

  useEffect(() => {
    if (smartlists?.length > 0) {
      return;
    }
    FunderListService.getAllWithFullAccess().then((r) => {
      r.onSuccess(setFunderLists);
      r.notifyErrorIfPresent();
    });
  }, []);

  const onClickNameOption = (name: string) => {
    setNewSmartListName(name);
  };

  const onChangeName = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewSmartListName(e.target.value);
  };

  const onClickIaStar = () => {
    setLoadingNames(true);
    setGeneratedNames([]);

    const context: any = { origin };

    if (origin === GeneratedNameOrigin.ADVANCED_SEARCH && getMappedFilters) {
      context.context = cleanObject(getMappedFilters());
    }

    if (origin === GeneratedNameOrigin.SMARTLIST && getFunderUuids) {
      context.funders = getFunderUuids();
    }

    debouncer.executeLastCall(() => {
      FunderListService.generateSmartListNames(context)
        .then((r) => {
          r.onSuccess((r) => {
            setGeneratedNames(r.content.names);
          });
          r.onError(() => {});
        })
        .finally(() => {
          setLoadingNames(false);
        });
    });
  };

  const deleteFromList = (funderListExternalId: string) => {
    if (!funder?.uuid || !funderListExternalId) {
      return;
    }

    buttonDebouncer.executeLastCall(() => {
      setLoadingAdding((prev) => ({ ...prev, [funderListExternalId]: true }));

      FunderListService.deleteFunder(funder?.uuid, funderListExternalId)
        .then((r) => {
          r.onSuccess((r) => {
            setFunderSmartlists(r);
          });
          r.notifyErrorIfPresent();
        })
        .finally(() =>
          setLoadingAdding((prev) => ({
            ...prev,
            [funderListExternalId]: false,
          }))
        );
    });
  };

  const getFunderSmartlists = () => {
    FunderListService.getByUserAndFunderUUID(funder?.uuid).then((r) => {
      r.onSuccess(setFunderSmartlists);
      r.notifyErrorIfPresent();
    });
  };

  const onAddFundersToExistingFunderList = (funderListExternalId: string) => {
    if (!funderListExternalId) {
      return;
    }
    buttonDebouncer.executeLastCall(() => {
      const selectedFunder = getFunderUuids();

      setLoadingAdding((prev) => ({ ...prev, [funderListExternalId]: true }));
      FunderListService.addFunders(funderListExternalId, selectedFunder)
        .then((res) => {
          res.onSuccess(() => {
            NotificationService.info(t("detail.saveToList.contactsAddedSuccess"));
            !hideAlreadyInLists && getFunderSmartlists();
          });
          res.notifyErrorIfPresent();
        })
        .finally(() =>
          setLoadingAdding((prev) => ({
            ...prev,
            [funderListExternalId]: false,
          }))
        );
    });
  };

  const alreadyInFunderlistsExternalIds = funderSmartlists.map((f) => f.externalId) || [];

  const onCreateNewFunderListHandler = () => {
    onCreateNewFunderList(newSmartListName || "");
  };

  const saveToSmartlistsButton = () => {
    return (
      <span>
        <CommonButton
          disabled={loading || !hasSmartListsCreation}
          onClick={onClick}
          aria-owns={anchor ? id : undefined}
          aria-haspopup="true"
        >
          {loading ? <CircularProgress size={20} sx={{ margin: "2px 50px" }} /> : t("detail.saveToList.buttonText")}
        </CommonButton>
      </span>
    );
  };

  return (
    <>
      {iconButton ? (
        <RoundButton defaultBackgroundColor="transparent" onClick={onClick} aria-haspopup="true">
          <MoreHoriz />
        </RoundButton>
      ) : !hasSmartListsCreation ? (
        <LazoTooltip title={getAddOnMessage(AddOnObjectKeyType.SMARTLISTS, "denied")} offset={[0, 50]}>
          {saveToSmartlistsButton()}
        </LazoTooltip>
      ) : (
        saveToSmartlistsButton()
      )}
      <StyledMenu id={id} anchorEl={anchor} open={Boolean(anchor)} onClose={handleCloseMenu}>
        <Container onMouseLeave={closeOnMouseLeave && handleCloseMenu} className="menu-container">
          <Container className="list-container">
            {funderLists
              ?.filter((f) => !hideAlreadyInLists || !alreadyInFunderlistsExternalIds.includes(f.externalId))
              .map((f) => (
                <Container className="smart-list-item" key={f.externalId}>
                  <Typography>{f.name}</Typography>

                  {loadingAdding[f.externalId] ? (
                    <>
                      <CircularProgress size={"10px"} color={"info"} />
                    </>
                  ) : alreadyInFunderlistsExternalIds.includes(f?.externalId) ? (
                    <SavedContainer
                      onClick={() => {
                        deleteFromList(f?.externalId);
                      }}
                    >
                      <Check />
                      <Typography>{t("detail.saveToList.saved")}</Typography>
                      <Box className="underline" />
                    </SavedContainer>
                  ) : (
                    <AddIcon
                      onClick={() => {
                        onAddFundersToExistingFunderList(f.externalId);
                        !iconButton && handleCloseMenu();
                      }}
                      sx={{
                        borderRadius: "50%",
                        backgroundColor: "transparent",
                        fontSize: "22px",
                        color: Colors.LAZO_PURPLE,
                        cursor: "pointer",
                      }}
                    />
                  )}
                </Container>
              ))}
          </Container>
          <Box sx={{ borderBottom: `${Colors.LIGHT_GRAY_DEDEE1} solid 1px` }}></Box>
          {origin != null ? (
            <NameInputContainer>
              <StyledTextBox
                value={newSmartListName}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={onChangeName}
                autoFocus
                type="text"
                placeholder={t("detail.saveToList.createSmartlistPlaceholder")}
                startAdornment={
                  <InputAdornment
                    sx={{
                      paddingLeft: "5px !important",
                    }}
                    position="start"
                  >
                    <LazoTooltip offset={[0, 24]} title={t("detail.saveToList.aiTooltip")}>
                      <StarButton onClick={onClickIaStar}>
                        <SimpleStarIcon
                          sx={{
                            fill: Colors.BUTTON_HOVER_PURPLE,
                            marginRight: "1px",
                          }}
                        />
                      </StarButton>
                    </LazoTooltip>
                  </InputAdornment>
                }
              />

              {loadingNames ? (
                <StyledList>
                  {[1, 2, 3].map((item) => (
                    <ListItem key={item}>
                      <Box className="list-item">
                        <LazoCheckbox disabled />
                        <Skeleton sx={{ width: "90%" }} />
                      </Box>
                    </ListItem>
                  ))}
                </StyledList>
              ) : (
                generatedNames.length > 0 && (
                  <>
                    <List>
                      {generatedNames.map((name) => (
                        <ListItem key={name}>
                          <Box className="list-item">
                            <LazoCheckbox
                              className="checkbox"
                              checked={name === newSmartListName}
                              onClick={() => {
                                onClickNameOption(name);
                              }}
                            />
                            <LazoTooltip title={name}>
                              <Typography>{name}</Typography>
                            </LazoTooltip>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )
              )}
              <CommonButton
                onClick={onCreateNewFunderListHandler}
                disabled={newSmartListName == null || newSmartListName.trim() === ""}
                customVariant="light-purple-background"
              >
                <Typography sx={{ fontSize: "12px" }}>{t("detail.saveToList.createSmartlistButton")}</Typography>
              </CommonButton>
            </NameInputContainer>
          ) : (
            <>
              <CommonButton onClick={onCreateNewFunderListHandler} customVariant="light-purple-background">
                <Typography sx={{ fontSize: "12px" }}>{t("detail.saveToList.createSmartlistButton")}</Typography>
              </CommonButton>
            </>
          )}
        </Container>
      </StyledMenu>
    </>
  );
};

export default SaveToListButton;

function cleanObject(obj: Object) {
  return Object.entries(obj)
    .filter(([_, value]) => {
      return Array.isArray(value) ? value.length > 0 : value;
    })
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
}

const Container = styled(Box)(() => ({
  width: "16rem",
  padding: "0.75rem",
  "&.menu-container": {
    width: "21.875rem",
    borderRadius: "0.5rem",
    border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    rowGap: "16px",
    display: "flex",
    flexDirection: "column",
    "& > div.list-container": {
      padding: "0px",
      borderRadius: "0.5rem",
      maxHeight: "15.625rem",
      overflowY: "auto",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      "& > div": {
        display: "flex",
        padding: "0px",
        width: "100%",
        justifyContent: "space-between",
      },
    },
  },
  "&.smart-list-item": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    minHeight: "2.1875rem",
    whiteSpace: "nowrap",
    maxWidth: "310px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    transition: "all 0.2s ease",
    paddingInline: "10px !important",
    "&:hover": {
      backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
    },
    "& > p": {
      fontSize: "12px",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
}));

const StyledTextBox = styled(InputBase)(() => ({
  [`&.${inputBaseClasses.root}`]: {
    overflow: "hidden",
    borderRadius: "0.375rem",
    border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
    fontSize: "12px",
    height: "32px",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "row",
    // columnGap: "0.5rem",
    transition: "all 0.2s ease",
  },
  [`&.${inputBaseClasses.focused}`]: {
    border: `${Colors.LAZO_PURPLE} solid 1px`,
  },
}));

const StyledMenu = styled(Menu)(() => ({
  [`& .${listClasses.root}`]: {
    padding: "0px",
    borderRadius: "0.5rem",
  },
  [`& .${paperClasses.root}`]: {
    marginTop: "5px",
    borderRadius: "0.5rem",
    boxShadow: "0px 0.25rem 0.675rem 0px #00000029",
  },
}));

const NameInputContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  rowGap: "12px",
  "& ul": { padding: "0px" },
  "&  li": { padding: "0px", height: "30px" },
  "& div.list-item": {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    columnGap: "0.375rem",
    alignItems: "center",
    "& .MuiButtonBase-root": { padding: "0px" },
    "& p": {
      fontSize: "12px",
      maxWidth: "280px",
      overflowX: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
}));

const SavedContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  columnGap: "0.25rem",
  borderBottom: "1px solid blactransparentk",
  transition: "all 0.2s ease",
  position: "relative",
  "& div.underline": {
    width: "100%",
    height: "1px",
    backgroundColor: "transparent",
    position: "absolute",
    bottom: 0,
    transition: "all 0.2s ease",
  },
  "&:hover": {
    cursor: "pointer",
    "& div.underline": {
      backgroundColor: "black",
    },
  },
  "& p": {
    fontSize: "0.75rem",
  },
}));

const StarButton = styled(Box)(() => ({
  cursor: "pointer",
  height: "25px",
  width: "25px",
  display: "grid",
  placeItems: "center",
  borderRadius: "50%",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: Colors.LAZO_PURPLE,
    "& svg": {
      fill: "#fff",
    },
  },
}));

const StyledList = styled(List)(() => ({
  width: "100%",
  padding: "0px",
  "& li": {
    width: "100%",
  },
  "&.MuiList-root ": {
    padding: "0px",
  },
}));
